import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";

const PCOL = { P1: "bg-red-600 text-white", P2: "bg-orange-500 text-white", P3: "bg-amber-100 text-amber-800", P4: "bg-slate-100 text-slate-600" };
const SCOL = {
  NEW: "bg-sky-100 text-sky-800", VALIDATED: "bg-indigo-100 text-indigo-800", "PENDING APPROVAL": "bg-violet-100 text-violet-800",
  ASSIGNED: "bg-blue-100 text-blue-800", "IN PROGRESS": "bg-cyan-100 text-cyan-800", "PENDING USER": "bg-amber-100 text-amber-800",
  "PENDING VENDOR": "bg-amber-100 text-amber-800", "PENDING PROCUREMENT": "bg-amber-100 text-amber-800", RESOLVED: "bg-emerald-100 text-emerald-800",
  CLOSED: "bg-slate-200 text-slate-700", CANCELLED: "bg-slate-100 text-slate-500 line-through", REOPENED: "bg-rose-100 text-rose-800",
};

export const PriorityBadge = ({ p }) => <span className={`inline-flex rounded px-1.5 py-0.5 text-[11px] font-semibold ${PCOL[p] || PCOL.P4}`} data-testid="sd-priority">{p}</span>;
export const StatusBadge = ({ s }) => <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${SCOL[s] || "bg-slate-100"}`} data-testid="sd-status">{s}</span>;

const left = (due) => {
  const h = (new Date(due) - Date.now()) / 36e5;
  if (h < 0) return null;
  return h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${h.toFixed(1)}h` : `${Math.round(h / 24)}d`;
};

export const SlaChip = ({ t }) => {
  const s = t.sla || {};
  if (["CLOSED", "CANCELLED", "RESOLVED"].includes(t.status)) {
    const bad = s.resolution_breached || s.response_breached;
    return <span className={`text-[11px] ${bad ? "text-red-600" : "text-emerald-700"}`} data-testid="sd-sla">{bad ? "SLA missed" : "SLA met"}</span>;
  }
  if (s.paused_at) return <span className="text-[11px] text-amber-700 inline-flex items-center gap-1" data-testid="sd-sla"><Icons.PauseCircle className="h-3 w-3" />SLA paused</span>;
  const key = s.responded_at ? "resolution" : "response";
  const l = left(s[`${key}_due`]);
  const warn = s[`warned_${key}`];
  return (
    <span className={`text-[11px] inline-flex items-center gap-1 ${!l || s[`${key}_breached`] ? "text-red-600 font-semibold" : warn ? "text-amber-600" : "text-slate-500"}`} title={fmtDateTime(s[`${key}_due`])} data-testid="sd-sla">
      <Icons.Timer className="h-3 w-3" />{l ? `${key === "response" ? "Respond" : "Resolve"} in ${l}` : `${key === "response" ? "Response" : "Resolution"} breached`}
    </span>
  );
};

let catCache = null;
export const useSd = () => {
  const [me, setMe] = useState(null);
  const [cat, setCat] = useState(catCache);
  useEffect(() => {
    api.get("/servicedesk/me").then(({ data }) => setMe(data)).catch(() => setMe({}));
    if (!catCache) api.get("/servicedesk/catalog").then(({ data }) => { catCache = data; setCat(data); }).catch(() => {});
  }, []);
  return { me, cat };
};

export const errText = (e, d = "Something went wrong") => { const x = e.response?.data?.detail; return typeof x === "string" ? x : d; };

export const Kpi = ({ label, value, icon, tone = "text-slate-900", testid }) => {
  const I = Icons[icon] || Icons.Circle;
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-xs text-slate-500">{label}<I className="h-4 w-4 text-slate-400" /></div>
      <div className={`text-2xl font-semibold mt-1 tabular-nums ${tone}`}>{value ?? "—"}</div>
    </div>
  );
};
